import type { Dispatch } from 'react';
import { useMemo, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';

import { JoinGroupButton } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/TopicsWrapper/JoinGroupButton';
import RequestGroupModal from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/TopicsWrapper/RequestGroupModal';
import { GoToGroupButtonWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicActions/GoToGroupButton';
import { TopicCardWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicCard';
import { TopicDescriptionWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicDescription';
import { TopicDetailsModalWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicDetailsModal';
import { getTopicImmersiveMenuHref } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicItem';
import { useTopicActionButton } from 'hb-react/shared/apps/TopicsIndexV2/hooks/useTopicActionButton';
import type { ITopic } from 'hb-react/shared/apps/TopicsIndexV2/types';

// implementation inherited from app/assets/javascripts/react/shared/apps/TopicsIndexV2/components/TopicCard.jsx
const BuilderTopicCard = ({
  isBackOffice,
  isConnected,
  networkRootUrl,
  setTopics,
  topic,
  topics,
}: {
  isBackOffice: boolean;
  isConnected: boolean;
  networkRootUrl?: string | null;
  setTopics: Dispatch<ITopic[]>;
  topic: ITopic;
  topics: ITopic[];
}) => {
  const [displayDetailsModal, setDisplayDetailsModal] = useState(false);

  const {
    approval_id: approvalId,
    id,
    description,
    description_visible_in_frontoffice: descriptionVisibleInFrontOffice,
    landing_page: landingPage,
    name,
    white_label_url: whiteLabelUrl,
  } = topic;

  const [requestApproval, setRequestApproval] = useState({
    id: approvalId,
  });

  const { buttonLabel, buttonA11yLabel } = useTopicActionButton(topic ?? {});

  const target = whiteLabelUrl ? '_blank' : undefined;

  const href = useMemo(() => {
    if (isBackOffice) {
      return '#';
    }
    if (whiteLabelUrl) {
      return landingPage
        ? getTopicImmersiveMenuHref(whiteLabelUrl, landingPage, id)
        : landingPage;
    }

    return landingPage ? `${networkRootUrl}${landingPage}` : networkRootUrl;
  }, [id, isBackOffice, landingPage, networkRootUrl, whiteLabelUrl]);

  const [displayRequestModal, setDisplayRequestModal] = useState(false);

  return (
    <TopicCardWithoutContext
      areActionsDisabled={isBackOffice}
      href={href}
      isConnectedUser
      setDisplayDetailsModal={setDisplayDetailsModal}
      topic={topic}
    >
      {!topic?.closed && (
        <Box flex={1} key="join-button" mt={3} mx={4}>
          <JoinGroupButton
            isBackOffice={isBackOffice}
            isConnected={isConnected}
            labelButton={buttonLabel}
            labelA11yButton={buttonA11yLabel}
            setDisplayRequestModal={setDisplayRequestModal}
            setTopics={setTopics}
            topic={topic}
            topics={topics}
          />
        </Box>
      )}
      <Box flex={1} key="go-to-group-button" mt={3} mx={4}>
        <GoToGroupButtonWithoutContext
          href={href}
          target={target}
          topicName={topic?.name}
        />
      </Box>
      <TopicDescriptionWithoutContext
        canRemoveDescriptionPlaceholder={false}
        description={description}
        descriptionVisibleInFrontOffice={descriptionVisibleInFrontOffice}
        isConnectedUser={isConnected}
        name={name}
        setDisplayDetailsModal={setDisplayDetailsModal}
      />
      {displayDetailsModal && (
        <TopicDetailsModalWithoutContext
          isConnectedUser={isConnected}
          setDisplayDetailsModal={setDisplayDetailsModal}
          topic={topic}
        >
          <Box ml="4" flexDirection="column" width={{ _: '283px', m: '209px' }}>
            <JoinGroupButton
              isBackOffice={isBackOffice}
              isConnected={isConnected}
              labelButton={buttonLabel}
              labelA11yButton={buttonA11yLabel}
              setDisplayRequestModal={setDisplayRequestModal}
              setTopics={setTopics}
              topic={topic}
              topics={topics}
            />
            <Box mt="4">
              <GoToGroupButtonWithoutContext
                forceDefaultLink
                href={href}
                target={target}
                topicName={topic?.name}
              />
            </Box>
          </Box>
        </TopicDetailsModalWithoutContext>
      )}
      {displayRequestModal && (
        <RequestGroupModal
          href={href}
          requestApproval={requestApproval}
          setDisplayRequestModal={setDisplayRequestModal}
          setRequestApproval={setRequestApproval}
          setTopics={setTopics}
          target={target}
          topic={topic}
          topics={topics}
        />
      )}
    </TopicCardWithoutContext>
  );
};

export default BuilderTopicCard;
